<script>
    /* States */
    import { setExternalDataState } from "@app/store/externalDataStore.svelte.js";
    import { explorerState } from '@app/store/explorerStore.svelte.js';
    import { authState } from "@app/store/authStore.svelte.js";
    import { showModal } from "@app/store/modalStore.svelte.js";
    import { Circle } from "svelte-loading-spinners";
    import { toast } from "@zerodevx/svelte-toast";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import { saveLastExplorerLocationFromRecord } from "@app/lib/explorerLastLocation.js";
    import workflowDict from "@cclib/dict/workflow.js";
    import * as m from "@app/paraglide/messages.js";
    
    /* Firebase */
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";
    import "firebase/functions";

    const STATES = workflowDict.states;
    let loading = $state(false);
    let userId = firebase.auth().currentUser.uid;
    let userEmail = firebase.auth().currentUser.email;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = db.collection("users").doc(userId).collection("storage");
    let teamDbRef = teamId ? db.collection("teams").doc(teamId).collection("storage") : null;
    let storageRef = storage.ref();
    let homeStorageBasePath = `users/${userId}/projects/`;
    let teamStorageBasePath = teamId ? "teams/" + teamId + "/storage/" : null;
    
    // Helper function to check if a folder should be visible to current user based on group permissions
    function canUserAccessFolder(folder) {
        // Admin and Manager roles always have access to all folders
        if (authState.user && (authState.user.role === "admin" || authState.user.role === "manager")) {
            return true;
        }
        
        // If folder has no groups assigned, it's accessible to all team members
        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }
        
        // Check if user is a member of any of the folder's assigned groups
        if (authState.user && authState.user.groups) {
            return folder.groups.some(groupId => authState.user.groups.includes(groupId));
        }
        
        // If user has no groups, they can't access group-restricted folders
        return false;
    }

    // Helper function to check if a project record should be visible based on its parent folder permissions
    async function canUserAccessRecord(record) {
        // Personal records are always accessible
        if (record.rootDir === "personal") {
            return true;
        }
        
        // If record has no locationId, it's at root level and accessible
        if (!record.locationId) {
            return true;
        }
        
        // For team records with locationId, check parent folder permissions
        if (record.rootDir === "team" && teamDbRef) {
            try {
                const parentFolderDoc = await teamDbRef.doc(record.locationId).get();
                
                // If parent folder doesn't exist, don't show the record
                if (!parentFolderDoc.exists) {
                    return false;
                }
                
                const parentFolder = parentFolderDoc.data();
                return canUserAccessFolder(parentFolder);
            } catch (error) {
                console.error("Error checking parent folder permissions:", error);
                return false;
            }
        }
        
        return true;
    }
    
    // Icon mapping for different record types
    const iconMap = {
        "project": "bi-file-earmark-play",
        "team project": "bi-people",
        "work order": "bi-clipboard-check"
    };
    const taskRecordTypes = ["project", "team project", "work order"];

    // Filter records to only include project types and organize by state
    let filteredRecords = $derived(explorerState.records.filter(record => 
        (record.type === "project" || record.type === "team project" || record.type === "work order")
        && record.state !== "Archived"
    ));
    
    // Create a map of state to records
    let recordsByState = $derived(STATES.reduce((acc, state) => {
        acc[state] = filteredRecords.filter(record => record.state === state);
        return acc;
    }, {}));

    // Also create a group for records with no state
    let recordsWithNoState = $derived(filteredRecords.filter(record => !record.state));

    // Track the current drag operation
    let draggedRecord = null;
    let sourceState = null;
    let sourceIndex = null;
    let dragOverColumn = null;
    let dragOverIndex = null;

    // Handler for starting a drag operation
    function handleDragStart(event, record, state, index) {
        // Don't allow drag in save mode
        if (explorerState.context === "save") {
            event.preventDefault();
            return;
        }
        
        // Don't allow dragging locked records (unless owned by current user)
        if (record.locked && record.lockedBy !== userEmail) {
            event.preventDefault();
            toast.push(m.explorer_kanban_move_locked_toast({ name: record.name, lockedBy: record.lockedBy }), {
                classes: ["toast-warning"],
            });
            return;
        }
        
        draggedRecord = record;
        sourceState = state;
        sourceIndex = index;
        
        // Set data for the drag operation
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/json", JSON.stringify({
            recordId: record.id,
            sourceState: state,
            sourceIndex: index
        }));
        
        // Add dragging class for styling
        event.target.classList.add("dragging");
    }

    // Handler for dragging over a column
    function handleColumnDragOver(event, state) {
        if (!draggedRecord) return;
        
        event.preventDefault();
        dragOverColumn = state;
        event.currentTarget.classList.add("drag-over");
    }
    
    // Handler for dragging over a card
    function handleCardDragOver(event, state, index) {
        if (!draggedRecord) return;
        
        event.preventDefault();
        dragOverColumn = state;
        dragOverIndex = index;
        
        const rect = event.currentTarget.getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        
        // Determine if the drag is over the top or bottom half of the card
        if (event.clientY < middle) {
            event.currentTarget.classList.add("drag-above");
            event.currentTarget.classList.remove("drag-below");
        } else {
            event.currentTarget.classList.add("drag-below");
            event.currentTarget.classList.remove("drag-above");
        }
    }
    
    // Handler for leaving a drop target
    function handleDragLeave(event) {
        event.currentTarget.classList.remove("drag-over");
        event.currentTarget.classList.remove("drag-above");
        event.currentTarget.classList.remove("drag-below");
    }
    
    // Handler for ending a drag operation
    function handleDragEnd(event) {
        event.target.classList.remove("dragging");
        draggedRecord = null;
        sourceState = null;
        sourceIndex = null;
        dragOverColumn = null;
        dragOverIndex = null;
        
        // Remove drag-over classes from all elements
        document.querySelectorAll(".drag-over, .drag-above, .drag-below").forEach(el => {
            el.classList.remove("drag-over", "drag-above", "drag-below");
        });
    }
    
    // Handler for dropping on a column (to change state)
    async function handleColumnDrop(event, targetState) {
        event.preventDefault();
        event.currentTarget.classList.remove("drag-over");
        
        if (!draggedRecord) return;
        
        try {
            // If target state is the same as source state, do nothing
            if (targetState === sourceState) return;
            
            // Update the record's state in the database and locally
            await updateRecordState(draggedRecord, targetState);
            
            // No need to call getRecords() as we've updated locally
            
        } catch (error) {
            console.error("Error handling column drop:", error);
            toast.push(m.explorer_kanban_update_state_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }
    
    // Handler for dropping on a card (to reorder or change state)
    async function handleCardDrop(event, targetState, targetIndex) {
        event.preventDefault();
        event.currentTarget.classList.remove("drag-above");
        event.currentTarget.classList.remove("drag-below");
        
        if (!draggedRecord) return;
        
        try {
            // Determine if we're changing state or just reordering
            if (targetState !== sourceState) {
                // Update record state in database and locally
                await updateRecordState(draggedRecord, targetState);
            } else if (targetIndex !== sourceIndex) {
                // Reordering within the same state
                // This is visual only as we don't persist order in the database
                
                // Create a copy of the records for the state
                const stateRecords = [...recordsByState[targetState]];
                
                // Remove the record from its original position
                const [record] = stateRecords.splice(sourceIndex, 1);
                
                // Insert it at the new position
                stateRecords.splice(
                    targetIndex > sourceIndex ? targetIndex - 1 : targetIndex, 
                    0, 
                    record
                );
                
                // Update the recordsByState object
                recordsByState[targetState] = stateRecords;
            }
            
            // No need to call getRecords() as we've updated locally
            
        } catch (error) {
            console.error("Error handling card drop:", error);
            toast.push(m.explorer_kanban_update_record_failed_toast({ error: error.message }), {
                classes: ["toast-danger"],
            });
        }
    }

    // Update a record's state in the database
    async function updateRecordState(record, newState) {        
        try {                      
            // Determine which database reference to use
            const dbRef = record.rootDir === "team" ? teamDbRef : homeDbRef;
            if (!dbRef) {
                throw new Error(m.explorer_kanban_db_ref_error());
            }
            
            // Update the state and modification timestamp in database
            await dbRef.doc(record.id).update({
                state: newState,
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
            });
            
            // Update the record locally in explorerState
            const currentTime = firebase.firestore.Timestamp.fromDate(new Date());
            
            // Find and update the record in the local array
            explorerState.records = explorerState.records.map(item => {
                if (item.id === record.id && item.rootDir === record.rootDir) {
                    return {
                        ...item,
                        state: newState,
                        updatedOn: currentTime
                    };
                }
                return item;
            });
            
            // Show success message
            toast.push(m.explorer_kanban_state_updated_toast({ name: record.name, state: newState }), {
                classes: ["toast-success"],
            });
            
        } catch (error) {
            console.error("Error updating record state:", error);
            throw error;
        } finally {
            loading = false;
        }
    }

    // Fetch records based on root directory and filters
    async function getRecords() {
        try {
            loading = true;
            explorerState.records = [];
            explorerState.selectedRecords = [];
            
            // Handle special rootDir values
            if (explorerState.rootDir === "recent") {
                // Fetch recent records from both personal and team storage
                let recentRecords = [];
                
                // Personal storage
                let personalSnapshot = await homeDbRef
                    .orderBy("updatedOn", "desc")
                    .limit(100)
                    .get();
                    
                personalSnapshot.forEach((doc) => {
                    // Create a unique composite ID using original ID and source
                    const record = doc.data();
                    recentRecords.push({
                        ...record, 
                        rootDir: "personal",
                        uniqueId: `personal-${record.id}`
                    });
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .orderBy("updatedOn", "desc")
                        .limit(100)
                        .get();
                        
                    teamSnapshot.forEach((doc) => {
                        // Create a unique composite ID using original ID and source
                        const record = doc.data();
                        recentRecords.push({
                            ...record, 
                            rootDir: "team",
                            uniqueId: `team-${record.id}`
                        });
                    });
                }
                
                // Filter to only include projects and work orders
                recentRecords = recentRecords.filter(record => 
                    record.type === "project" || record.type === "team project" || record.type === "work order"
                );
                
                // Filter team records by checking parent folder permissions
                const filteredRecords = [];
                for (const record of recentRecords) {
                    if (await canUserAccessRecord(record)) {
                        filteredRecords.push(record);
                    }
                }
                
                // Sort by updatedOn timestamp
                filteredRecords.sort((a, b) => {
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                explorerState.records = filteredRecords.slice(0, 100);
                
            } else if (explorerState.rootDir === "starred") {
                // Fetch starred records from both personal and team storage
                let starredRecords = [];
                
                // Personal storage
                let personalSnapshot = await homeDbRef
                    .where("star", "==", true)
                    .limit(100)
                    .get();
                    
                personalSnapshot.forEach((doc) => {
                    // Create a unique composite ID using original ID and source
                    const record = doc.data();
                    starredRecords.push({
                        ...record, 
                        rootDir: "personal",
                        uniqueId: `personal-${record.id}`
                    });
                });
                
                // Team storage (if available)
                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .where("star", "==", true)
                        .limit(100)
                        .get();
                        
                    teamSnapshot.forEach((doc) => {
                        // Create a unique composite ID using original ID and source
                        const record = doc.data();
                        starredRecords.push({
                            ...record, 
                            rootDir: "team", 
                            uniqueId: `team-${record.id}`
                        });
                    });
                }
                
                // Filter to only include projects and work orders
                starredRecords = starredRecords.filter(record => 
                    record.type === "project" || record.type === "team project" || record.type === "work order"
                );
                
                // Filter team records by checking parent folder permissions
                const filteredRecords = [];
                for (const record of starredRecords) {
                    if (await canUserAccessRecord(record)) {
                        filteredRecords.push(record);
                    }
                }
                
                // Sort by updatedOn timestamp
                filteredRecords.sort((a, b) => {
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA; // Newest first
                });
                
                explorerState.records = filteredRecords.slice(0, 100);
                
            } else if (explorerState.rootDir === "tasks") {
                let taskRecords = [];

                let personalSnapshot = await homeDbRef
                    .where("assignedTo", "==", userEmail)
                    .limit(100)
                    .get();

                personalSnapshot.forEach((doc) => {
                    const record = doc.data();
                    const recordId = record.id || doc.id;
                    taskRecords.push({
                        ...record,
                        id: recordId,
                        rootDir: "personal",
                        uniqueId: `personal-${recordId}`
                    });
                });

                if (teamDbRef) {
                    let teamSnapshot = await teamDbRef
                        .where("assignedTo", "==", userEmail)
                        .limit(100)
                        .get();

                    teamSnapshot.forEach((doc) => {
                        const record = doc.data();
                        const recordId = record.id || doc.id;
                        taskRecords.push({
                            ...record,
                            id: recordId,
                            rootDir: "team",
                            uniqueId: `team-${recordId}`
                        });
                    });
                }

                taskRecords = taskRecords.filter(record => taskRecordTypes.includes(record.type) && record.state !== "Archived");

                const filteredRecords = [];
                for (const record of taskRecords) {
                    if (await canUserAccessRecord(record)) {
                        filteredRecords.push(record);
                    }
                }

                filteredRecords.sort((a, b) => {
                    const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                    const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                    return timeB - timeA;
                });

                explorerState.records = filteredRecords.slice(0, 100);

            } else {
                // For personal or team storage, use locationId to respect folder structure
                // Get locationId from the last element in navHistory, or use null if at root
                const currentFolderId = explorerState.navHistory.length > 0 
                    ? explorerState.navHistory[explorerState.navHistory.length - 1].id 
                    : null;
                    
                // Only get project types (not folders) for Kanban view
                let projectTypes = ["project", "team project", "work order"];
                let queries = [];
                
                if (explorerState.rootDir === "personal") {
                    // For personal storage, get project types with matching locationId
                    queries = projectTypes.map(type => 
                        homeDbRef
                            .where("type", "==", type)
                            .where("locationId", "==", currentFolderId)
                            .orderBy("name", "asc")
                            .get()
                    );
                } else if (teamDbRef) {
                    // For team storage, get project types with matching locationId
                    queries = projectTypes.map(type => 
                        teamDbRef
                            .where("type", "==", type)
                            .where("locationId", "==", currentFolderId)
                            .orderBy("name", "asc")
                            .get()
                    );
                }
                
                const queryResults = await Promise.all(queries);
                
                // Combine results
                const records = [];
                queryResults.forEach(snapshot => {
                    snapshot.forEach(doc => {
                        const record = doc.data();
                        const source = explorerState.rootDir;
                        records.push({
                            ...record, 
                            rootDir: source,
                            uniqueId: `${source}-${record.id}`
                        });
                    });
                });
                
                explorerState.records = records;
            }
            
            // console.log("Kanban view records loaded:", explorerState.records.length);
        } catch(err){
            console.error(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        } finally {
            loading = false;
        }
    }

    // Open a record when clicked
    async function openRecord(record) {
        try {
            loading = true;
            
            // Check if project is locked by someone else
            if (record.locked && record.lockedBy !== userEmail) {
                // Show notification about locked project
                await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.ok,
                    title: m.explorer_kanban_locked_title(),
                    text: m.explorer_kanban_locked_text({ lockedBy: record.lockedBy }),
                    icon: 'info',
                    confirmButtonText: m.explorer_kanban_locked_confirm(),
                });
            }
            
            // Determine the storage path based on whether it's personal or team
            const projectFilePath = record.rootDir === "team" 
                ? `${teamStorageBasePath}${record.id}` 
                : `${homeStorageBasePath}${record.id}`;
            
            // Get the URL for the file
            let url;
            if (record.rootDir === "team") {
                const result = await firebase.functions().httpsCallable("v8GetDownloadLink")(projectFilePath);
                url = result.data;
            } else {
                url = await storageRef.child(projectFilePath).getDownloadURL();
            }
            
            // Fetch the file content
            const response = await fetch(url);
            const fileData = await response.json();
            fileData.rootDir = record.rootDir;
            
            // Process based on record type
            if (record.type === "work order") {
                openWorkOrder(fileData, record);
            } else if (record.type === "project") {
                openProjectFile(fileData, record);
            } else if (record.type === "team project") {
                openTeamProjectFile(fileData, record);
            }
            
        } catch (error) {
            console.error(`Error opening ${record.type}:`, error);
            toast.push(m.explorer_kanban_open_failed_toast({ type: record.type, error: error.message }), {
                classes: ["toast-danger"],
            });
        } finally {
            loading = false;
        }
    }

    function openProjectFile(description, record){
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    async function openTeamProjectFile(description, selectedRecord) {
        try {
            loading = true;
            // Extract team ID and project ID
            const projectTeamId = description.teamId;
            
            if (!projectTeamId || !teamId) {
                throw new Error("Missing team or project ID in file");
            }
            
            // console.log(`Looking up share codes for team project: ${projectTeamId} in team: ${teamId}`);
            
            // Check for project snapshots first (to ensure there's data to share)
            const snapshotsRef = db.collection("teams").doc(teamId)
                .collection("projects").doc(projectTeamId)
                .collection("snapshots");
                
            const snapshotsQuery = await snapshotsRef.limit(1).get();
            
            if (snapshotsQuery.empty) {
                // console.log("No snapshots found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Look for share codes that reference this project
            const sharesRef = db.collection("teams").doc(teamId).collection("shares");
                
            const sharesQuery = await sharesRef
                .where("projectTeamId", "==", projectTeamId)
                .limit(1)
                .get();            
            
            if (sharesQuery.empty) {
                // console.log("No share codes found for team project, falling back to project file import");
                fallbackToProjectFileImport(description, selectedRecord);
                return;
            }
            
            // Get the first share code
            const shareDoc = sharesQuery.docs[0];
            const shareId = shareDoc.id;
            
            // console.log(`Found share code: ${shareId}`);
            
            // Update URL with share code and open teamProjectImport modal
            const url = new URL(window.location.href);
            url.searchParams.set("id", shareId);
            window.history.pushState({}, '', url);
            
            toast.push(m.explorer_kanban_open_team_project_toast(), {
                classes: ["toast-info"],
            });

            saveLastExplorerLocationFromRecord(userId, selectedRecord);
            
            showModal("teamProjectImport");
            
        } catch (error) {
            console.error("Error processing team project:", error);
            fallbackToProjectFileImport(description, selectedRecord);
        }
    }

    function fallbackToProjectFileImport(description, record) {
        console.log("Falling back to project file import method");
        toast.push(m.explorer_kanban_fallback_project_import_toast(), {
            classes: ["toast-info"],
        });
        
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Project File",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call loadProjectFile modal
        showModal("loadProjectFile");
    }

    function openWorkOrder(description, record) {
        // Add folderId to description object
        description.folderId = record.locationId;

        saveLastExplorerLocationFromRecord(userId, record);
        
        setExternalDataState({
            platform: "Work Order",
            status : "importing",
            rawDescription: JSON.stringify(description)
        });
        
        //call workOrderImport modal
        showModal("workOrderImport");
    }

    // Formats a date for display
    function formatDate(timestamp) {
        if (!timestamp) return "-";
        const date = timestamp.toDate();
        return date.toLocaleDateString();
    }

    function getDescriptionPreview(description) {
        if (!description) return "";

        const preview = String(description);
        return preview.length > 250 ? `${preview.slice(0, 250)}...` : preview;
    }
    
    // Function to select a record when clicked
    function selectRecord(id) {
        // For Kanban view, we only support single selection
        explorerState.selectedRecords = [id];
    }
    
    // Initial load of records
    getRecords();
</script>

{#if loading}
<div class="loading-container d-flex justify-content-center align-items-center w-100 h-100">
    <Circle
    size="75"
    color="#1eb4b2"
    unit="px"
    duration="1s"
    ></Circle>
</div>
{:else}
<div class="kanban-container">
    <div class="kanban-board" role="application" aria-label={m.explorer_kanban_board_aria()}>
        <!-- Column for records with no state -->
        <div 
            class="kanban-column"
            role="region"
            aria-label={m.explorer_kanban_no_state_label()}
            ondragover={(e) => handleColumnDragOver(e, null)}
            ondragleave={handleDragLeave}
            ondrop={(e) => handleColumnDrop(e, null)}
        >
            <div class="column-header">
                <h5>{m.explorer_kanban_no_state_label()}</h5>
                <span class="badge bg-secondary">{recordsWithNoState.length}</span>
            </div>
            <div class="column-content" role="list">
                {#each recordsWithNoState as record, index (record.uniqueId || record.id)}
                    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
                    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                    <div 
                        class="kanban-card card {explorerState.selectedRecords.includes(record.id) ? 'selected' : ''}"
                        role="article"
                        aria-roledescription="draggable item"
                        tabindex="0"
                        draggable="true"
                        aria-label={record.name}
                        onclick={() => selectRecord(record.id)}
                        ondragstart={(e) => handleDragStart(e, record, null, index)}
                        ondragend={handleDragEnd}
                        ondragover={(e) => handleCardDragOver(e, null, index)}
                        ondragleave={handleDragLeave}
                        ondrop={(e) => handleCardDrop(e, null, index)}
                        ondblclick={() => openRecord(record)}
                        onkeydown={(e) => e.key === 'Enter' && openRecord(record)}
                    >
                        <div class="card-header d-flex align-items-center py-2">
                            <i class="bi {iconMap[record.type]} me-2"></i>
                            <span class="card-title">{record.name}</span>
                            <div class="ms-auto d-flex">
                                {#if record.locked}<i class="bi bi-lock-fill text-danger ms-1"></i>{/if}
                                {#if record.star}<i class="bi bi-star-fill text-warning ms-1"></i>{/if}
                            </div>
                        </div>
                        <div class="card-body py-2">
                            {#if record.description}
                                <p class="card-description text-body-secondary">
                                    {getDescriptionPreview(record.description)}
                                </p>
                            {/if}
                            <div class="card-info">
                                <div><i class="bi bi-person text-secondary me-1"></i> <span class="text-body-secondary">{record.owner || m.explorer_kanban_unknown_owner()}</span></div>
                                {#if record.assignedTo}
                                    <div><i class="bi bi-person-check text-secondary me-1"></i> <span class="text-body-secondary">{record.assignedTo}</span></div>
                                {/if}
                                <div><i class="bi bi-clock text-secondary me-1"></i> <span class="text-body-secondary">{formatDate(record.updatedOn)}</span></div>
                            </div>
                        </div>
                    </div>
                {/each}
            </div>
        </div>
        
        <!-- Columns for each workflow state -->
        {#each STATES as state (state)}
            <div 
                class="kanban-column"
                role="region"
                aria-label={state}
                ondragover={(e) => handleColumnDragOver(e, state)}
                ondragleave={handleDragLeave}
                ondrop={(e) => handleColumnDrop(e, state)}
            >
                <div class="column-header">
                    <h5>{state}</h5>
                    {#if state === "Archived"}
                        <i class="bi bi-archive-fill text-secondary ms-2" title={m.explorer_kanban_archived_label()}></i>
                    {:else}
                    <span class="badge bg-secondary">{recordsByState[state].length}</span>
                    {/if}
                </div>
                <div class="column-content" role="list">
                    {#each recordsByState[state] as record, index (record.uniqueId || record.id)}
                        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
                        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
                        <div 
                            class="kanban-card card {explorerState.selectedRecords.includes(record.id) ? 'selected' : ''}"
                            role="article"
                            aria-roledescription="draggable item" 
                            tabindex="0"
                            draggable="true"
                            aria-label={record.name}
                            onclick={() => selectRecord(record.id)}
                            ondragstart={(e) => handleDragStart(e, record, state, index)}
                            ondragend={handleDragEnd}
                            ondragover={(e) => handleCardDragOver(e, state, index)}
                            ondragleave={handleDragLeave}
                            ondrop={(e) => handleCardDrop(e, state, index)}
                            ondblclick={() => openRecord(record)}
                            onkeydown={(e) => e.key === 'Enter' && openRecord(record)}
                        >
                            <div class="card-header d-flex align-items-center py-2">
                                <i class="bi {iconMap[record.type]} me-2"></i>
                                <span class="card-title">{record.name}</span>
                                <div class="ms-auto d-flex">
                                    {#if record.locked}<i class="bi bi-lock-fill text-danger ms-1"></i>{/if}
                                    {#if record.star}<i class="bi bi-star-fill text-warning ms-1"></i>{/if}
                                </div>
                            </div>
                            <div class="card-body py-2">
                                {#if record.description}
                                    <p class="card-description text-body-secondary">
                                        {getDescriptionPreview(record.description)}
                                    </p>
                                {/if}
                                <div class="card-info">
                                    <div><i class="bi bi-person text-secondary me-1"></i> <span class="text-body-secondary">{record.owner || m.explorer_kanban_unknown_owner()}</span></div>
                                    {#if record.assignedTo}
                                        <div><i class="bi bi-person-check text-secondary me-1"></i> <span class="text-body-secondary">{record.assignedTo}</span></div>
                                    {/if}
                                    <div><i class="bi bi-clock text-secondary me-1"></i> <span class="text-body-secondary">{formatDate(record.updatedOn)}</span></div>
                                </div>
                            </div>
                        </div>
                    {/each}
                </div>
            </div>
        {/each}
    </div>
</div>
{/if}

<style lang="css">
    .loading-container {
        height: 80vh;
    }
    
    .kanban-container {
        height: 80vh;
        overflow-x: auto;
        padding: 1rem 0;
    }
    
    .kanban-board {
        display: flex;
        min-height: 100%;
        gap: 1rem;
    }
    
    .kanban-column {
        flex: 0 0 280px;
        background-color: var(--bs-light);
        border-radius: 0.375rem;
        display: flex;
        flex-direction: column;
        max-height: 100%;
        border: 1px solid var(--bs-border-color);
    }
    
    .kanban-column.drag-over {
        background-color: var(--bs-primary-bg-subtle);
        border: 1px dashed var(--bs-primary);
    }
    
    .column-header {
        padding: 0.75rem;
        background-color: var(--bs-light-bg-subtle);
        border-bottom: 1px solid var(--bs-border-color);
        border-radius: 0.375rem 0.375rem 0 0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: sticky;
        top: 0;
        z-index: 1;
    }
    
    .column-header h5 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--bs-body-color);
    }
    
    .column-content {
        padding: 0.75rem;
        overflow-y: auto;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
    
    .kanban-card {
        cursor: pointer;
        user-select: none;
        position: relative;
        background-color: var(--bs-card-bg, var(--bs-body-bg));
        border-color: var(--bs-card-border-color, var(--bs-border-color));
    }
    
    .kanban-card:hover {
        box-shadow: 0 0.125rem 0.25rem rgba(var(--bs-body-color-rgb), 0.15);
    }
    
    .kanban-card.dragging {
        opacity: 0.5;
    }
    
    .kanban-card.drag-above {
        border-top: 2px solid var(--bs-primary);
        margin-top: -2px;
    }
    
    .kanban-card.drag-below {
        border-bottom: 2px solid var(--bs-primary);
        margin-bottom: -2px;
    }
    
    .kanban-card.selected {
        border-color: var(--bs-primary);
        border-width: 2px;
    }
    
    .card-title {
        flex-grow: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--bs-body-color);
    }
    
    .card-info {
        font-size: 0.8rem;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .card-description {
        display: -webkit-box;
        margin-bottom: 0.35rem;
        overflow: hidden;
        overflow-wrap: anywhere;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
        line-clamp: 4;
        white-space: normal;
        font-size: 0.75rem;
        line-height: 1.25;
    }
    
    .card-info div {
        display: flex;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    /* Overriding Bootstrap card styles for our specific use case */
    .card-header {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        background-color: var(--bs-card-cap-bg, rgba(var(--bs-body-color-rgb), 0.03));
    }
    
    .card-body {
        padding: 0.5rem;
    }
</style>
